import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Alert,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Workspaces as GroupIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { apiClient } from '../../services/apiClient';
import { extractErrorMessage } from '../../utils/errorUtils';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { LoadingScreen } from '../../components/common/LoadingScreen';

interface LocationMember {
  id: number;
  icao_code: string;
  name: string;
  country_code: string | null;
}

interface LocationGroupDetail {
  id: number;
  name: string;
  type: string;
  description: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  members: LocationMember[];
}

interface LocationOption {
  id: number;
  icao_code: string;
  name: string;
  country_code: string | null;
}

export default function LocationGroupDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [group, setGroup] = useState<LocationGroupDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Add locations dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [allLocations, setAllLocations] = useState<LocationOption[]>([]);
  const [selectedLocationIds, setSelectedLocationIds] = useState<Set<number>>(new Set());
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [locationFilter, setLocationFilter] = useState('');

  // Remove confirmation
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<LocationMember | null>(null);

  useEffect(() => {
    loadGroup();
  }, [id]);

  const loadGroup = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.get<LocationGroupDetail>(`/admin/location-groups/${id}`);
      setGroup(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load location group');
    } finally {
      setLoading(false);
    }
  };

  // --- Edit group ---

  const openEditDialog = () => {
    if (!group) return;
    setEditName(group.name);
    setEditDescription(group.description || '');
    setEditError(null);
    setEditDialogOpen(true);
  };

  const handleEdit = async () => {
    try {
      setEditLoading(true);
      setEditError(null);
      await apiClient.put(`/admin/location-groups/${id}`, {
        name: editName,
        description: editDescription || null,
      });
      setEditDialogOpen(false);
      await loadGroup();
    } catch (err: unknown) {
      setEditError(extractErrorMessage(err, 'Failed to update location group'));
    } finally {
      setEditLoading(false);
    }
  };

  // --- Add locations ---

  const openAddDialog = async () => {
    setAddDialogOpen(true);
    setSelectedLocationIds(new Set());
    setAddError(null);
    setLocationFilter('');

    try {
      setLoadingLocations(true);
      const locations = await apiClient.get<LocationOption[]>('/admin/locations');
      const memberIds = new Set(group?.members.map((m) => m.id) || []);
      const available = locations.filter((loc: LocationOption) => !memberIds.has(loc.id));
      setAllLocations(available);
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Failed to load locations');
    } finally {
      setLoadingLocations(false);
    }
  };

  const toggleLocation = (locationId: number) => {
    setSelectedLocationIds((prev) => {
      const next = new Set(prev);
      if (next.has(locationId)) {
        next.delete(locationId);
      } else {
        next.add(locationId);
      }
      return next;
    });
  };

  const handleAddLocations = async () => {
    if (selectedLocationIds.size === 0) return;

    try {
      setAddLoading(true);
      setAddError(null);
      await apiClient.post(`/admin/location-groups/${id}/members`, {
        location_ids: Array.from(selectedLocationIds),
      });
      setAddDialogOpen(false);
      await loadGroup();
    } catch (err: unknown) {
      setAddError(extractErrorMessage(err, 'Failed to add locations'));
    } finally {
      setAddLoading(false);
    }
  };

  const filteredLocations = allLocations.filter((loc) => {
    const search = locationFilter.toLowerCase();
    return (
      loc.icao_code.toLowerCase().includes(search) ||
      loc.name.toLowerCase().includes(search) ||
      (loc.country_code || '').toLowerCase().includes(search)
    );
  });

  // --- Remove member ---

  const handleRemoveMember = (member: LocationMember) => {
    setSelectedMember(member);
    setRemoveDialogOpen(true);
  };

  const handleConfirmRemove = async () => {
    if (!selectedMember) return;

    try {
      await apiClient.delete(`/admin/location-groups/${id}/members/${selectedMember.id}`);
      setRemoveDialogOpen(false);
      setSelectedMember(null);
      await loadGroup();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove location from group');
    }
  };

  if (loading) {
    return <LoadingScreen message="Loading location group..." />;
  }

  if (error || !group) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 2 }}>
          <Alert severity="error">
            {error || 'Location group not found'}
          </Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/location-groups')}
            sx={{ mt: 2 }}
          >
            Back to Location Groups
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/location-groups')}
            sx={{ mb: 2 }}
          >
            Back to Location Groups
          </Button>
          <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1.5, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
              <GroupIcon sx={{ fontSize: { xs: 24, sm: 32 }, color: 'primary.main' }} />
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                    {group.name}
                  </Typography>
                  <Chip
                    label={group.type}
                    color={group.type === 'system' ? 'info' : 'default'}
                    variant="outlined"
                    size="small"
                  />
                </Box>
                {group.description && (
                  <Typography variant="body2" color="text.secondary">
                    {group.description}
                  </Typography>
                )}
              </Box>
            </Box>
            {group.type !== 'system' && (
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={openEditDialog}
                size="small"
              >
                Edit
              </Button>
            )}
          </Box>
        </Box>

        {/* Members Section */}
        <Box sx={{ mb: 2, display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
            Locations ({group.members.length})
          </Typography>
          {group.type !== 'system' && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openAddDialog} size="small">
              Add Locations
            </Button>
          )}
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ICAO Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Country</TableCell>
                {group.type !== 'system' && (
                  <TableCell align="right">Actions</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {group.members.map((member) => (
                <TableRow key={member.id}>
                  <TableCell>{member.icao_code}</TableCell>
                  <TableCell>{member.name}</TableCell>
                  <TableCell>{member.country_code || '-'}</TableCell>
                  {group.type !== 'system' && (
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveMember(member)}
                        title="Remove from group"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {group.members.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              No locations in this group
            </Typography>
          </Box>
        )}

        {/* Edit Group Dialog */}
        <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Location Group</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {editError && (
                <Alert severity="error" onClose={() => setEditError(null)}>
                  {editError}
                </Alert>
              )}
              <TextField
                label="Group Name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="Description"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                fullWidth
                multiline
                rows={2}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)} disabled={editLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleEdit}
              variant="contained"
              disabled={editLoading || !editName.trim()}
            >
              {editLoading ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Locations Dialog (Checklist) */}
        <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add Locations to Group</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
              {addError && (
                <Alert severity="error" onClose={() => setAddError(null)}>
                  {addError}
                </Alert>
              )}
              {loadingLocations ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <TextField
                    placeholder="Filter locations..."
                    value={locationFilter}
                    onChange={(e) => setLocationFilter(e.target.value)}
                    size="small"
                    fullWidth
                    sx={{ mb: 1 }}
                  />
                  <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                    <Table size="small">
                      <TableBody>
                        {filteredLocations.map((loc) => (
                          <TableRow
                            key={loc.id}
                            hover
                            sx={{ cursor: 'pointer' }}
                            onClick={() => toggleLocation(loc.id)}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedLocationIds.has(loc.id)}
                                onChange={() => toggleLocation(loc.id)}
                              />
                            </TableCell>
                            <TableCell>{loc.icao_code}</TableCell>
                            <TableCell>{loc.name}</TableCell>
                            <TableCell>{loc.country_code || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {filteredLocations.length === 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                        No locations available
                      </Typography>
                    )}
                  </Box>
                  {selectedLocationIds.size > 0 && (
                    <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                      {selectedLocationIds.size} location(s) selected
                    </Typography>
                  )}
                </>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddDialogOpen(false)} disabled={addLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleAddLocations}
              variant="contained"
              disabled={addLoading || selectedLocationIds.size === 0}
            >
              {addLoading ? 'Adding...' : `Add ${selectedLocationIds.size} Location(s)`}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Remove Member Confirmation Dialog */}
        <ConfirmDialog
          open={removeDialogOpen}
          title="Remove Location"
          message={`Are you sure you want to remove "${selectedMember?.icao_code} - ${selectedMember?.name}" from this group?`}
          confirmText="Remove"
          confirmColor="error"
          onConfirm={handleConfirmRemove}
          onCancel={() => {
            setRemoveDialogOpen(false);
            setSelectedMember(null);
          }}
        />
      </Box>
    </Container>
  );
}
